/**
 * Wall-clock time in somebody else's zone.
 *
 * A `datetime-local` control holds a wall-clock reading with no offset, and
 * `new Date('2026-09-20T08:00')` resolves it in the *browser's* zone. That is
 * the right answer only when the person at the keyboard is standing in the
 * same zone as the appointment. A coordinator in New York booking an install
 * for a customer in Phoenix types 08:00 meaning the customer's 08:00, and the
 * browser would book it four hours early — a van arriving before dawn.
 *
 * These helpers resolve a wall-clock reading against an explicit IANA zone
 * using the `Intl` database the platform already ships, so no dependency and
 * no offset table of our own is involved.
 */

/** The shape a `datetime-local` control holds, to the minute. */
const LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Reads the parts a zone shows for an instant.
 *
 * @param at the instant, in epoch milliseconds
 * @param timeZone an IANA zone name
 * @returns the wall-clock reading that zone shows, as epoch milliseconds of
 *   the same reading interpreted in UTC
 * @throws RangeError when the zone name is not one the platform knows
 */
function wallClockOf(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at));
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour'),
    read('minute'),
    read('second'),
  );
}

/**
 * How far a zone stood from UTC at an instant.
 *
 * @param at the instant, in epoch milliseconds
 * @param timeZone an IANA zone name
 * @returns the offset in milliseconds, positive east of Greenwich
 */
function offsetAt(at: number, timeZone: string): number {
  return wallClockOf(at, timeZone) - at;
}

/**
 * Whether the platform recognises a zone name.
 *
 * @param timeZone the candidate name
 * @returns true when `Intl` accepts it
 */
export function isTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
    return true;
  } catch {
    return false;
  }
}

/**
 * Turns a wall-clock reading in a named zone into an instant.
 *
 * The offset is looked up at a first guess and then re-checked, because the
 * reading itself decides which side of a daylight-saving boundary it falls on.
 * A reading inside a spring-forward gap does not exist in that zone; it
 * resolves forward to the first instant after the gap, which is what a
 * dispatcher means by "the van arrives then".
 *
 * @param local what a `datetime-local` control holds, `YYYY-MM-DDTHH:mm`
 * @param timeZone an IANA zone name; an unknown name falls back to the
 *   browser's own zone rather than throwing at a coordinator mid-booking
 * @returns the ISO-8601 instant, or null when the reading is empty or malformed
 */
export function zonedToInstant(local: string, timeZone: string): string | null {
  const match = LOCAL_PATTERN.exec(local.trim());
  if (match === null) {
    return null;
  }
  const zone = isTimeZone(timeZone) ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [, year, month, day, hour, minute, second] = match;
  const reading = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? '0'),
  );
  if (Number.isNaN(reading)) {
    return null;
  }
  const firstOffset = offsetAt(reading, zone);
  const candidate = reading - firstOffset;
  const secondOffset = offsetAt(candidate, zone);
  if (secondOffset === firstOffset) {
    return new Date(candidate).toISOString();
  }
  const retry = reading - secondOffset;
  const thirdOffset = offsetAt(retry, zone);
  if (thirdOffset === secondOffset) {
    return new Date(retry).toISOString();
  }
  // The reading falls in a spring-forward hole: no instant shows it. Taking the
  // smaller offset lands on the first instant after the clocks moved.
  return new Date(reading - Math.min(secondOffset, thirdOffset)).toISOString();
}

/**
 * Turns an instant into the wall-clock reading a named zone shows for it.
 *
 * This is the inverse of {@link zonedToInstant} and is what fills a
 * `datetime-local` control from a stored instant, so the field reads back the
 * customer's time rather than the coordinator's.
 *
 * @param instant the ISO-8601 instant
 * @param timeZone an IANA zone name
 * @returns `YYYY-MM-DDTHH:mm`, or an empty string when the instant is unusable
 */
export function instantToZoned(instant: string, timeZone: string): string {
  const at = Date.parse(instant);
  if (Number.isNaN(at)) {
    return '';
  }
  const zone = isTimeZone(timeZone) ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date(wallClockOf(at, zone)).toISOString().slice(0, 16);
}

/**
 * The calendar date a zone is currently on.
 *
 * `new Date().toISOString().slice(0, 10)` is the UTC date, which for a
 * Chicago workspace flips to tomorrow at 7 pm; defaults that mean "today"
 * must come from here instead.
 *
 * @param timeZone an IANA zone name; an unknown name falls back to the browser's zone
 * @param now the instant to read, in epoch milliseconds (defaults to the current time)
 * @returns `YYYY-MM-DD` as that zone's wall calendar shows it
 */
export function todayInZone(timeZone: string, now: number = Date.now()): string {
  const zone = isTimeZone(timeZone) ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date(wallClockOf(now, zone)).toISOString().slice(0, 10);
}

/**
 * Renders an instant the way the customer's own clock reads it.
 *
 * @param instant the ISO-8601 instant
 * @param timeZone an IANA zone name
 * @returns a readable date and time, followed by the zone's short name
 */
export function formatInZone(instant: string, timeZone: string): string {
  const at = Date.parse(instant);
  if (Number.isNaN(at)) {
    return '—';
  }
  const zone = isTimeZone(timeZone) ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  // `timeZoneName` cannot be combined with the style shorthands, so the zone's
  // short name is formatted separately and appended.
  const shown = new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: zone,
  }).format(new Date(at));
  const named = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    timeZoneName: 'short',
  })
    .formatToParts(new Date(at))
    .find((part) => part.type === 'timeZoneName');
  return named === undefined ? shown : shown + ' ' + named.value;
}
